import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { supabase } from '../integrations/supabase/client';
import { useProperty } from '../context/PropertyContext';

interface AutoUpdateConfig {
  id: string;
  enabled: boolean;
  morning_time: string;
  evening_time: string;
  variation_type: string;
  custom_variations: string[];
}

export const AutoUpdateManager = () => {
  const [config, setConfig] = useState<AutoUpdateConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { refreshProperties } = useProperty();

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('auto_update_config')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setConfig({
          ...data,
          custom_variations: Array.isArray(data.custom_variations) 
            ? data.custom_variations.filter((v): v is string => typeof v === 'string')
            : []
        });
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar configurações de auto-update",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!config) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('auto_update_config')
        .upsert({
          ...config,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Configurações salvas com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar configurações",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const runManualUpdate = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.rpc('auto_update_properties');

      if (error) throw error;

      // Recarregar as propriedades para mostrar as mudanças
      await refreshProperties();

      toast({
        title: "Sucesso",
        description: "Atualização manual executada com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao executar atualização manual:', error);
      toast({
        title: "Erro",
        description: "Erro ao executar atualização manual",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6">Carregando configurações...</div>;
  }

  if (!config) {
    return <div className="p-6">Erro ao carregar configurações</div>;
  }

  return (
    <div className="space-y-6 p-6 bg-white dark:bg-gray-800 rounded-lg shadow">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          🔄 Auto-Update OLX
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Sistema automático para manter seus imóveis sempre no topo das buscas da OLX
        </p>
      </div>

      <div className="grid gap-6">
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Status do Sistema
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {config.enabled ? '✅ Ativo - Atualizações automáticas habilitadas' : '❌ Inativo'}
            </p>
          </div>
          <Button
            variant={config.enabled ? "destructive" : "default"}
            onClick={() => setConfig({...config, enabled: !config.enabled})}
          >
            {config.enabled ? 'Desativar' : 'Ativar'}
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Horário da Manhã
            </label>
            <input
              type="time"
              value={config.morning_time}
              onChange={(e) => setConfig({...config, morning_time: e.target.value})}
              className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Horário da Noite
            </label>
            <input
              type="time"
              value={config.evening_time}
              onChange={(e) => setConfig({...config, evening_time: e.target.value})}
              className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
        </div>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
            Como funciona?
          </h4>
          <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
            <li>• Sistema roda automaticamente 2x por dia (conforme horários definidos)</li>
            <li>• Adiciona frases motivacionais aleatórias nas descrições dos imóveis</li>
            <li>• Remove frases antigas e adiciona novas para manter o conteúdo fresco</li>
            <li>• Atualiza o campo "updated_at" para que a OLX reconheça como conteúdo novo</li>
            <li>• Seus imóveis aparecem com prioridade nas buscas da OLX</li>
          </ul>
        </div>

        <div className="flex gap-4">
          <Button
            onClick={saveConfig}
            disabled={saving}
            className="flex-1"
          >
            {saving ? 'Salvando...' : 'Salvar Configurações'}
          </Button>
          <Button
            onClick={runManualUpdate}
            disabled={saving}
            variant="outline"
            className="flex-1"
          >
            {saving ? 'Executando...' : 'Executar Agora'}
          </Button>
        </div>

        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
          <h4 className="font-semibold text-amber-900 dark:text-amber-100 mb-2">
            ⚠️ Importante
          </h4>
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Este sistema adiciona pequenas variações nas descrições dos imóveis para manter eles sempre atualizados.
            As frases são profissionais e adequadas para o mercado imobiliário.
          </p>
        </div>
      </div>
    </div>
  );
};